'use client'

import { Link as I18nLink } from '@/i18n/navigation'
import NextLink from 'next/link'
import { useIsI18nContext } from '@/hooks/useIsI18nContext'
import { useSafeTranslations } from '@/hooks/useSafeTranslations'
import { useActivityVisibility } from '@/hooks/useActivityVisibility'
import { adventures } from '@/lib/data/adventures'
import { lodges } from '@/lib/data/lodges'
import './Footer.css'

export default function Footer() {
  const isI18n = useIsI18nContext()
  const Link = isI18n ? I18nLink : NextLink
  const t = useSafeTranslations('footer')
  const { isVisible } = useActivityVisibility()

  const lodgeFooterKey: Record<string, string> = {
    'apex-mountain-penthouse': 'apexMountainPenthouse',
    'apex-parva-cave': 'apexParvaCave',
    'apex-premium-lodge': 'apexPremiumLodge',
    'apex-yelcho-patagonia': 'apexYelchoPatagonia',
    'apex-lakefront-serenity': 'apexLakefrontSerenity',
    'apex-patagonia-lodge': 'apexPatagoniaLodge',
  }

  return (
    <footer className="footer">

      {/* ═══ BAND 1: Contact (Get in Touch) ─ NOW FIRST ═══ */}
      <div className="footer-band-contact">
        <hr className="footer-amber-rule" />
        <div className="footer-inner footer-contact-top">
          <div className="footer-contact-row">

            <div className="footer-contact-left">
              <span className="footer-contact-eyebrow">{t('getInTouch')}</span>
              <span className="footer-contact-heading">{t('contactTitle')}</span>
            </div>

            <div className="footer-contact-right">
              <a href="tel:+56991998708" className="footer-contact-phone">
                +56 9 9199 8708
              </a>

              <Link href="/contact" className="footer-contact-btn" prefetch={false}>
                {t('getInTouch')}
              </Link>

              <a
                href="https://www.instagram.com/apexexperiencecl/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('instagram')}
                className="footer-ig-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                  <path
                    fill="white"
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                  />
                </svg>
              </a>
            </div>

          </div>
        </div>
      </div>

      <hr className="footer-band-rule" />

      {/* ═══ BAND 2: Navigation (Lodges / Adventures / Book / About) ═══ */}
      <div className="footer-band-nav">
        <div className="footer-inner">
          <nav className="footer-nav-columns" aria-label={t('footerNavigation')}>

            <div className="footer-nav-col">
              <p className="footer-nav-heading">{t('lodges')}</p>
              <ul>
                {lodges.filter(l => isVisible(l.id, l.comingSoon)).map(lodge => (
                  <li key={lodge.id}>
                    <Link
                      href={`/lodges/${lodge.slug}`}
                      className={lodge.comingSoon ? 'coming-soon' : undefined}
                      prefetch={false}
                    >
                      {t(lodgeFooterKey[lodge.id])}
                      {lodge.comingSoon && (
                        <span className="footer-soon-label">{t('comingSoon')}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="footer-nav-col">
              <p className="footer-nav-heading">{t('adventures')}</p>
              <ul>
                {([
                  { id: 'resort-skiing', tKey: 'resortSkiing' },
                  { id: 'heli-skiing',   tKey: 'heliSkiing' },
                  { id: 'ski-touring',   tKey: 'skiTouring' },
                  { id: 'cat-skiing',    tKey: 'catSkiing' },
                  { id: 'fly-fishing',   tKey: 'flyFishing' },
                ] as const)
                  .filter(a => isVisible(a.id, adventures.find(adv => adv.id === a.id)?.hidden))
                  .map(a => (
                    <li key={a.id}>
                      <Link href={`/guided-adventures/${a.id}`} prefetch={false}>{t(a.tKey)}</Link>
                    </li>
                  ))}
              </ul>
            </div>

            <div className="footer-nav-col">
              <p className="footer-nav-heading">{t('about')}</p>
              <ul>
                <li><Link href="/why-apex" prefetch={false}>{t('whyApex')}</Link></li>
                <li><Link href="/contact"  prefetch={false}>{t('contact')}</Link></li>
              </ul>
            </div>

          </nav>
        </div>
      </div>

      {/* ═══ BAND 3: Copyright ═══ */}
      <div className="footer-band-copyright">
        <p>&copy;{t('copyright')}</p>
      </div>

    </footer>
  )
}
